/**
 * CreateAgreementPage - Full-page agreement creation flow
 *
 * This page provides the intentional, step-by-step agreement creation experience.
 * It can be accessed directly via /app/agreements/create or triggered from the
 * CreateOptionsModal.
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FlowWizard from '../../components/flows/FlowWizard';
import AgreementTypeStep from '../../components/flows/agreement/AgreementTypeStep';
import AgreementClientStep from '../../components/flows/agreement/AgreementClientStep';
import AgreementDetailsStep from '../../components/flows/agreement/AgreementDetailsStep';
import AgreementContentStep from '../../components/flows/agreement/AgreementContentStep';
import AgreementReviewStep from '../../components/flows/agreement/AgreementReviewStep';

// Define the flow steps with conditions
const AGREEMENT_STEPS = [
  {
    id: 'type',
    title: 'Create Agreement',
    subtitle: 'How would you like to create your agreement?',
    component: AgreementTypeStep,
    condition: true,
  },
  {
    id: 'client',
    title: 'Select Client',
    subtitle: 'Who is this agreement for?',
    component: AgreementClientStep,
    condition: true,
  },
  {
    id: 'details',
    title: 'Service Details',
    subtitle: 'What are you providing?',
    component: AgreementDetailsStep,
    // Only show for AI mode - manual mode goes straight to content
    condition: (data) => data.agreementType === 'ai',
  },
  {
    id: 'content',
    title: 'Agreement Content',
    subtitle: 'Write or generate your agreement',
    component: AgreementContentStep,
    // Show for AI and manual modes
    condition: (data) => data.agreementType === 'ai' || data.agreementType === 'manual',
  },
  {
    id: 'review',
    title: 'Review & Create',
    subtitle: 'Make sure everything looks right',
    component: AgreementReviewStep,
    condition: true,
  },
];

const CreateAgreementPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get initial data from URL params (e.g., when coming from a project or lead)
  const initialData = {
    agreementType: null,
    client: null,
    clientName: '',
    clientEmail: '',
    scope: '',
    price: '',
    currency: 'USD',
    timeline: new Date().toISOString().split('T')[0],
    venue: '',
    startTime: '',
    duration: '',
    specialRequirements: '',
    agreementTitle: '',
    agreementContent: '',
    // Pre-fill from URL params if available
    ...(searchParams.get('client') && {
      clientName: searchParams.get('client'),
    }),
    ...(searchParams.get('email') && {
      clientEmail: searchParams.get('email'),
    }),
    ...(searchParams.get('scope') && {
      scope: searchParams.get('scope'),
    }),
  };

  const handleComplete = (finalData) => {
    // Navigation happens in ReviewStep after agreement is created
  };

  const handleCancel = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="fixed inset-0 z-50 bg-white dark:bg-[#0A0A0A]">
      {/* Header with back button */}
      <div className="absolute top-0 left-0 right-0 h-16 flex items-center px-6 z-10">
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      {/* Flow */}
      <div className="pt-16 h-full">
        <FlowWizard
          steps={AGREEMENT_STEPS}
          initialData={initialData}
          onComplete={handleComplete}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default CreateAgreementPage;
